import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, signedInEmail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import Seo from "@/components/Seo";

const FILTERS = [
  { label: "Everything", value: "" },
  { label: "For Coaches", value: "coach" },
  { label: "For Parents", value: "parent" },
  { label: "For Players", value: "player" },
];

const AUDIENCE_BADGE = {
  coach: { label: "Coach", cls: "text-[#2D2D2D]" },
  parent: { label: "Parent", cls: "text-[#2E7D57]" },
  player: { label: "Player", cls: "text-[#DB9130]" },
};
const audBadge = (a) => AUDIENCE_BADGE[a] || { label: a, cls: "text-[#6E6E6E]" };

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [email, setEmail] = useState("");
  const [me, setMe] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data || []));
    signedInEmail().then((e) => { setMe(e); if (e) setEmail((cur) => cur || e); });
  }, []);

  const shown = products.filter((p) => !filter || p.audience.includes(filter));

  const buy = async () => {
    if (!active) return;
    setBusy(true);
    trackEvent("begin_checkout", { currency: "USD", value: active.amount / 100, items: [{ item_id: active.lookup_key, item_name: active.name }] });
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: active.lookup_key,
        origin_url: window.location.origin,
        email: email || null,
        source: "products",
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Couldn't start checkout. Try again.");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-testid="products-page">
      <Seo title="Digital Products" description="A small, quiet catalog of toolkits and playbooks worth owning — for youth-sports coaches and parents." path="/products" />
      <span className="kicker">Digital Products</span>
      <h1 className="mt-2 font-head font-semibold uppercase text-4xl lg:text-6xl text-[#1E1E1E] leading-[1.05]">The shelf</h1>
      <p className="mt-3 text-[#6E6E6E] max-w-2xl text-lg">
        A small, quiet catalog of things worth owning. No countdown timers, no fake discounts — just the toolkits and
        playbooks I actually use and hand to other coaches and parents.
      </p>

      <div className="mt-8 flex gap-2 border-b-2 border-[#D3CFC0] pb-4">
        {FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)} data-testid={`products-filter-${f.value || "all"}`}
            className={`px-4 py-2 font-head uppercase tracking-wide text-xs border-2 transition-colors ${filter === f.value ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-10 divide-y divide-[#D3CFC0] border-t border-[#D3CFC0]">
        {shown.map((p, i) => (
          <button key={p.lookup_key} onClick={() => { if (p.checkout === "page") { navigate(p.page); return; } setActive(p); setEmail(me); }} data-testid={`product-row-${p.lookup_key}`}
            className="group w-full text-left py-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 hover:bg-[#E7E3D9] transition-colors px-2 -mx-2">
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 mb-1">
                {p.audience.map((a) => (
                  <span key={a} data-testid={`product-audience-${a}`} className={`kicker border border-current px-1.5 py-0.5 ${audBadge(a).cls}`}>{audBadge(a).label}</span>
                ))}
                <span className="kicker text-[#E8A33D]">· {p.category_tag}</span>
              </div>
              <h3 className="font-head font-semibold text-xl text-[#1E1E1E] group-hover:text-[#2D2D2D] transition-colors">{p.name}</h3>
              <p className="mt-1 text-[#6E6E6E] text-sm leading-relaxed max-w-2xl">{p.blurb}</p>
            </div>
            <div className="font-head text-3xl text-[#1E1E1E] shrink-0">${p.amount / 100}</div>
          </button>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1A1A]/70" onClick={() => setActive(null)} data-testid="product-modal">
          <div className="bg-[#F3F1EC] border-2 border-[#1E1E1E] max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-6 border-b-2 border-[#D3CFC0]">
              <div>
                <div className="flex flex-wrap gap-2 mb-1">
                  {active.audience.map((a) => <span key={a} className={`kicker border border-current px-1.5 py-0.5 ${audBadge(a).cls}`}>{audBadge(a).label}</span>)}
                </div>
                <h2 className="font-head font-semibold text-2xl text-[#1E1E1E] leading-tight">{active.name}</h2>
              </div>
              <button onClick={() => setActive(null)} data-testid="product-modal-close" className="text-[#6E6E6E] hover:text-[#1E1E1E] shrink-0"><X size={22} /></button>
            </div>
            <div className="p-6">
              <p className="text-[#6E6E6E] leading-relaxed">{active.blurb}</p>
              <div className="mt-5">
                <span className="kicker">Inside</span>
                <ul className="mt-2 space-y-2">
                  {active.contents.map((c, i) => (
                    <li key={i} className="flex gap-2 text-[#1E1E1E] text-[15px]"><span className="text-[#E8A33D] font-head">›</span> {c}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-6 border-t border-[#D3CFC0] pt-5">
                <label className="kicker">Email for your receipt & download</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="product-email-input"
                  className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="you@email.com" />
                <button onClick={buy} disabled={busy} data-testid="product-buy-btn"
                  className="mt-4 w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
                  {busy ? "Starting checkout…" : `Buy for $${active.amount / 100}`}
                </button>
                <p className="mt-2 text-xs text-[#6E6E6E] text-center">Secure checkout via Stripe.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
